import React from "react";
import { Link } from "react-router-dom";
import { Settings, Trash2 } from "lucide-react";
import type { AlertDto } from "../lib/alerts-api.js";
import { getAlertEditRoute } from "../routes.js";
import { getSpecializationTitle } from "../constants/specializations.js";

export interface AlertCardProps {
  alert: AlertDto;
  canEdit: boolean;
  onDelete: (id: number) => void;
}

export function formatLanguageBadges(languages: string[]): string[] {
  if (!languages || languages.length === 0) {
    return ["ru/en"];
  }
  const norm = languages.map((l) => l.toLowerCase());
  const hasRu = norm.includes("ru") || norm.includes("russian") || norm.includes("русский");
  const hasEn = norm.includes("en") || norm.includes("english");
  if (hasRu && hasEn) {
    return ["ru/en"];
  }
  if (hasRu) return ["ru"];
  if (hasEn) return ["en"];
  return languages.length === 1 ? languages : ["ru/en"];
}

export function AlertCard({ alert, canEdit, onDelete }: AlertCardProps): React.ReactElement {
  return (
    <div
      data-testid={`alert-card-${alert.id}`}
      style={{
        backgroundColor: "var(--color-paper)",
        borderRadius: "var(--radius-card)",
        boxShadow: "var(--shadow-floating)",
        padding: "20px",
        marginBottom: "16px",
        display: "flex",
        flexDirection: "column",
        gap: "14px",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: "17px",
              fontWeight: 600,
              color: "var(--color-graphite)",
            }}
          >
            {getSpecializationTitle(alert.specialization)}
          </h3>
        </div>
      </div>

      {/* Filter Badges */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
        {alert.seniorities.map((s) => (
          <span
            key={s}
            style={{
              fontSize: "12px",
              fontWeight: 500,
              padding: "4px 10px",
              borderRadius: "var(--radius-pill)",
              backgroundColor: "var(--color-canvas)",
              color: "var(--color-graphite)",
            }}
          >
            {s}
          </span>
        ))}

        {formatLanguageBadges(alert.languages).map((l) => (
          <span
            key={l}
            data-testid={`alert-language-${l}`}
            style={{
              fontSize: "12px",
              fontWeight: 500,
              padding: "4px 10px",
              borderRadius: "var(--radius-pill)",
              backgroundColor: "var(--color-canvas)",
              color: "var(--color-graphite)",
            }}
          >
            {l}
          </span>
        ))}

        {alert.skills.map((skill) => (
          <span
            key={skill}
            style={{
              fontSize: "12px",
              fontWeight: 500,
              padding: "4px 10px",
              borderRadius: "var(--radius-pill)",
              backgroundColor: "var(--color-success-bg)",
              color: "var(--color-success)",
              border: "1px solid rgba(23, 115, 77, 0.2)",
            }}
          >
            +{skill}
          </span>
        ))}

        {alert.exclusions.map((exc) => (
          <span
            key={exc.value}
            style={{
              fontSize: "12px",
              fontWeight: 500,
              padding: "4px 10px",
              borderRadius: "var(--radius-pill)",
              backgroundColor: "var(--color-destructive-bg)",
              color: "var(--color-destructive)",
            }}
          >
            -{exc.value}
          </span>
        ))}

        {alert.seniorities.length === 0 &&
          alert.languages.length === 0 &&
          alert.skills.length === 0 &&
          alert.exclusions.length === 0 && (
            <span style={{ fontSize: "13px", color: "var(--color-slate)" }}>Все вакансии без фильтров</span>
          )}
      </div>

      {/* Action Buttons */}
      {canEdit && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: "8px",
            borderTop: "1px solid var(--color-frost-border)",
          }}
        >
          <Link
            to={getAlertEditRoute(alert.id)}
            data-testid={`edit-alert-link-${alert.id}`}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "13px",
              fontWeight: 600,
              color: "var(--color-graphite)",
              backgroundColor: "var(--color-canvas)",
              textDecoration: "none",
              padding: "8px 14px",
              borderRadius: "var(--radius-button)",
            }}
          >
            <Settings size={14} />
            Настроить
          </Link>

          <button
            data-testid={`delete-alert-btn-${alert.id}`}
            onClick={() => onDelete(alert.id)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "36px",
              height: "36px",
              borderRadius: "var(--radius-button)",
              backgroundColor: "var(--color-canvas)",
              color: "var(--color-destructive)",
              border: "none",
              cursor: "pointer",
            }}
            title="Удалить"
          >
            <Trash2 size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
